"use client"

import { useState, useEffect, useRef } from "react"
import "./MVPprocess.css"

const ScopingProcess = () => {
  const [activeTab, setActiveTab] = useState("planning")
  const headerRef = useRef(null)
  const textRef = useRef(null)
  const imageRef = useRef(null)
  const tabsRef = useRef(null)

  const tabContent = {
    businessCase: {
      title: "BUSINESS CASE",
      items: [
        "At Lattice Labs, the MVP starts with a 2-week business case to present the vision and goals of the project and clarify all necessary requirements. This step is based on main competitors and exploration of personas to then, finally, define a timeline."
      ],
    },
    projectPlan: {
      title: "PROJECT PLAN AND BACKLOG",
      items: [
        "We then follow with a master plan that evolves as product development takes place; this is a product backlog and a task list representing the various features that you want to implement on your product. Those will be contemplated on several sprints - rolling wave planning - that occur in fixed time frames afterward. The conclusion of each sprint generates feedback that will be used as input for the next round, which will happen as often as needed to complete the project plan.",
      ],
    },
    test: {
      title: "TEST & LAUNCH YOU PRODUCT",
      subtitle1: "1. HIGH-LEVEL ARCHITECTURE",
      subtitle2: "2. PROJECT PLAN",
      description:
        "Create a detailed roadmap that will be used to identify the main features the product will have and prioritize them.",
      items: [
        "After the MVP is finally launched, we want to ensure a smooth transition from the implementation stages to the production stage. We run intense testing of the product in real conditions and have the whole team - project manager, designers, and developers - solve any issues; this also helps them do an MVP Retrospective - by analysing the work that has been done during the MVP sprint  as well as through feedback loops, and plan consciously what should be the next mountain to climb.",
      ],
    },
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
          }
        })
      },
      { threshold: 0.1 },
    )

    const elements = [headerRef.current, textRef.current, imageRef.current, tabsRef.current]
    elements.forEach((el) => {
      if (el) observer.observe(el)
    })

    return () => {
      elements.forEach((el) => {
        if (el) observer.unobserve(el)
      })
    }
  }, [])

  return (
    <div className="scoping-container" id="process">
      <div className="container">
      <div className="scoping-wrapper">
        {/* Header */}
        <div className="header-section" ref={headerRef}>
          <h2 className="header1-title">
            OUR
            <br />
            <span className="highlight">MVP PROCESS</span>
            
            
          </h2>
        </div>

        {/* Main Content */}
        <div className="main-content">
          <div className="text-section" ref={textRef}>
            <p className="main-description">
              The scoping session starts from the planning stage from a Project Manager and the interaction from
              developers and designers to build a solid roadmap to give shape for your project and estimate it.
            </p>
          </div>
          <div className="image-section" ref={imageRef}>
            <img
              src="/process-scoping.webp?height=300&width=400"
              alt="Scoping Process Illustration"
              className="process-image"
            />
          </div>
        </div>

        {/* Tabs Section */}
        <div className="tabs-container" ref={tabsRef}>
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === "businessCase" ? "active" : ""}`}
              onClick={() => setActiveTab("businessCase")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 20h20M5 20V8.5M9 20v-9M13 20v-5.5M17 20v-9"></path>
                </svg>
              </div>
              BUSINESS CASE
            </button>
            <button
              className={`tab-button ${activeTab === "projectPlan" ? "active" : ""}`}
              onClick={() => setActiveTab("projectPlan")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </div>
              PROJECT PLAN AND BACKLOG
            </button>
            <button
              className={`tab-button ${activeTab === "test" ? "active" : ""}`}
              onClick={() => setActiveTab("test")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </div>
              TEST & LAUNCH YOU PRODUCT
            </button>
          </div>

          <div className="tab-content">
            <div className={`tab-panel ${activeTab === "businessCase" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.businessCase.title}</h3>
              <ul className="tab-list">
                {tabContent.businessCase.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`tab-panel ${activeTab === "projectPlan" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.projectPlan.title}</h3>
              <ul className="tab-list">
                {tabContent.projectPlan.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`tab-panel ${activeTab === "test" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.test.title}</h3>
              
              <ul className="tab-list">
                {tabContent.test.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <h3 className="cta-title">NEED A QUOTE FOR YOUR PROJECT?</h3>
          <p className="cta-description">
            Our team of business developers and project managers are ready to analyze your questions. Have a coffee,
            free time to explain us everything and get a quote for your project.
          </p>
          <button className="quote-button">GET A QUOTE</button>
        </div>
      </div>
      </div>
    </div>
  )
}

export default ScopingProcess;
